import React from 'react';
import { FiBookOpen, FiTrendingUp, FiCheckCircle, FiClock, FiUsers, FiZap, FiBarChart2, FiVideo, FiMessageSquare } from 'react-icons/fi';

const WhatIsLMS = () => {
  const lmsFeatures = [
    {
      icon: <FiBookOpen className="w-6 h-6" />,
      title: 'Course Content Management',
      description: 'An LMS allows teachers to create, organize, and deliver course content in one place—videos, documents, presentations, and interactive materials. No more sharing files via WhatsApp or email. Everything is organized, accessible, and searchable.',
      stat: '70% of teachers say organized content improves teaching effectiveness',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiVideo className="w-6 h-6" />,
      title: 'Video Lectures and Recordings',
      description: 'Teachers can upload pre-recorded lectures or conduct live classes. Students can watch recorded sessions anytime for revision or catch-up. This is especially valuable for students who miss classes or want to review difficult concepts.',
      stat: '85% of students prefer recorded lectures for revision',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiCheckCircle className="w-6 h-6" />,
      title: 'Assignments and Assessments',
      description: 'Teachers can create, distribute, and grade assignments digitally. Online quizzes and tests provide instant feedback. This reduces the paper burden and speeds up the assessment process significantly.',
      stat: '90% of teachers say online assessments save grading time',
      color: 'bg-amber-50'
    },
    {
      icon: <FiMessageSquare className="w-6 h-6" />,
      title: 'Discussion Forums and Collaboration',
      description: 'Students can participate in discussions, ask questions, and collaborate on projects through discussion forums. This builds community and encourages peer learning outside the classroom.',
      stat: '75% of students benefit from peer discussions in online forums',
      color: 'bg-rose-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Progress Tracking and Analytics',
      description: 'Teachers and administrators can track student progress—course completion, assignment submission, quiz scores, and engagement metrics. This data helps identify struggling students early.',
      stat: '80% of institutions track student engagement through LMS analytics',
      color: 'bg-purple-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Student and Teacher Collaboration',
      description: 'An LMS connects everyone in the learning ecosystem. Teachers can communicate with students, students can collaborate with peers, and parents can monitor progress. It breaks down classroom walls.',
      stat: '90% of students feel more connected when using an LMS',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Anytime, Anywhere Learning',
      description: 'Students can access course materials 24/7 from any device. Learning extends beyond the classroom.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Personalized Learning',
      description: 'Students learn at their own pace. They can revisit materials, skip ahead, or focus on areas needing improvement.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Reduced Teacher Workload',
      description: 'Automate content delivery, assignment collection, and grading. Teachers focus on teaching, not paperwork.',
      icon: <FiCheckCircle className="w-5 h-5" />
    },
    {
      title: 'Better Student Engagement',
      description: 'Interactive content, discussion forums, and gamification keep students engaged and motivated.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Data-Driven Teaching',
      description: 'Teachers can identify struggling students, understand learning patterns, and adapt their teaching accordingly.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Preparation for Future',
      description: 'Digital learning is the future. Students develop skills they will need in higher education and the workplace.',
      icon: <FiTrendingUp className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        A Learning Management System (LMS) is software that helps educational institutions create, manage, and deliver learning content. It's a digital classroom where teachers can share materials, assign work, and assess students—all online.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        For many Indian institutions, the question is no longer "Do we need an LMS?" but <strong className="text-slate-900">"How soon can we implement one?"</strong> The pandemic accelerated digital adoption, and students, parents, and teachers now expect online learning capabilities as a standard feature.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's what a Learning Management System is, why Indian institutions need one, and what to look for.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">85%</div>
          <div className="text-sm text-slate-500">Students prefer recorded lectures for revision</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">90%</div>
          <div className="text-sm text-slate-500">Teachers say online assessments save grading time</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">80%</div>
          <div className="text-sm text-slate-500">Institutions track engagement through LMS analytics</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Key Features of a Learning Management System</h2>

      {/* Features List */}
      {lmsFeatures.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Why Indian Institutions Need an LMS</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* LMS vs Traditional */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Traditional Classroom vs. LMS-Enhanced Learning</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Traditional Classroom</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Physical presence required</span></li>
              <li className="flex items-start gap-2">• <span>Learning materials are limited</span></li>
              <li className="flex items-start gap-2">• <span>No access to recordings</span></li>
              <li className="flex items-start gap-2">• <span>Manual assignments and grading</span></li>
              <li className="flex items-start gap-2">• <span>Limited student engagement tracking</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">LMS-Enhanced Learning</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Learn from anywhere</span></li>
              <li className="flex items-start gap-2">• <span>Rich, interactive content</span></li>
              <li className="flex items-start gap-2">• <span>Recorded lectures for revision</span></li>
              <li className="flex items-start gap-2">• <span>Automated assignments and grading</span></li>
              <li className="flex items-start gap-2">• <span>Real-time progress tracking</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Indian Context */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Indian Context</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Indian educational institutions face unique challenges—large class sizes, limited infrastructure, diverse student needs, and budget constraints. An LMS helps address these challenges by making learning more accessible and efficient.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          With the push for digital education under NEP 2020, LMS adoption is no longer optional. Institutions must embrace digital learning tools to stay relevant and competitive.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>LMS is not just about technology—it's about equity, accessibility, and quality.</strong>
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon's Integrated LMS is the Best Choice</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📚</div>
            <h4 className="font-bold text-slate-900 text-sm">Integrated with ERP</h4>
            <p className="text-xs text-slate-600">Seamless integration with academic records</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-First</h4>
            <p className="text-xs text-slate-600">Learn on any device, anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌐</div>
            <h4 className="font-bold text-slate-900 text-sm">Multi-Language</h4>
            <p className="text-xs text-slate-600">Content in multiple Indian languages</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">Competency-Based</h4>
            <p className="text-xs text-slate-600">Aligns with NEP 2020 outcomes</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Analytics Dashboard</h4>
            <p className="text-xs text-slate-600">Track student progress in real-time</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Low Bandwidth</h4>
            <p className="text-xs text-slate-600">Optimized for Indian network conditions</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">The Future of Learning is Digital</h3>
        <p className="text-indigo-800 leading-relaxed">
          Learning Management Systems are transforming education in India. They make learning accessible, engaging, and effective. For institutions, an LMS is not just a tool—it's a strategic investment in the future of education.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon comes with an integrated LMS that works seamlessly with your academic management system. No more separate platforms. No more integration headaches. Everything you need in one place.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for LMS.</strong> The future of learning—integrated, accessible, and affordable.
        </p>
      </div>
    </>
  );
};

export default WhatIsLMS;